import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  FormHelperText,
  Checkbox,
  TableContainer,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Grid,
  InputAdornment,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SettingsIcon from '@mui/icons-material/Settings';
import BusinessIcon from '@mui/icons-material/Business';
import TagIcon from '@mui/icons-material/Tag';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PeopleIcon from '@mui/icons-material/People';
import DevicesIcon from '@mui/icons-material/Devices';
import EmailIcon from '@mui/icons-material/Email';
import LanguageIcon from '@mui/icons-material/Language';
import StoreIcon from '@mui/icons-material/Store';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InfoIcon from '@mui/icons-material/Info';
import RestoreIcon from '@mui/icons-material/Restore';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import RefreshIcon from '@mui/icons-material/Refresh';
import BlockIcon from '@mui/icons-material/Block';
import SearchIcon from '@mui/icons-material/Search';
import { MuiTelInput } from 'mui-tel-input';
import { useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';
import { api } from '../lib/apiClient';
import { Application, CreateTenantPayload, Dealer, Tenant, UpdateTenantPayload } from '../types';
import { useAuth } from '../state/AuthContext';
import { useToast } from '../state/ToastContext';
import { DEFAULT_LOCALE, LOCALE_OPTIONS } from '../i18n/locales';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '../utils/errorUtils';
import OnboardingWizard from '../components/OnboardingWizard';
import { TablePaginationControl, usePagination } from '../components/TablePaginationControl';
import CustomButton from '../components/CustomButton';
import FormDialog from '../components/FormDialog';
import { TableSortLabel } from '@mui/material';
import { useSortableTable } from '../hooks/useSortableTable';
import { usePersistedColumns } from '../hooks/usePersistedColumns';
import { TableColumnSelector } from '../components/TableColumnSelector';
import { ExportMenu } from '../components/ExportMenu';
import { createTenantSchema, updateTenantSchema, manageAppsSchema } from '../schemas/tenant.schema';

interface TenantFormState {
  dealerId: string;
  name: string;
  code: string;
  locale: string;
  status: 'active' | 'inactive';
  masterUsername: string;
  masterEmail: string;
  masterDisplayName: string;
  licenseValidUntil: string;
  maxPeople: string;
  maxDevices: string;
  isDemo: boolean;
  createdAt: string;
  applicationIds: string[];
  applicationModules: Record<string, string[]>;
  moduleExpirations: Record<string, string>;
  logoData?: string | null;
  logoPreview?: string | null;
  phone: string;
  contactEmail: string;
}


const formatDateForInput = (value: string | null | undefined) => {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  return parsed.toISOString().slice(0, 10);
};

const formatDateForDisplay = (value: string, locale?: string) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString(locale ?? undefined);
};

import { CONFIG } from '../config';

const computeDemoLicenseFromDate = (base?: string) => {
  const reference = base ? new Date(base) : new Date();
  if (Number.isNaN(reference.getTime())) {
    reference.setTime(Date.now());
  }
  reference.setHours(0, 0, 0, 0);
  reference.setDate(reference.getDate() + CONFIG.DEMO_DURATION_DAYS);
  return reference.toISOString().slice(0, 10);
};
const todayStr = new Date().toISOString().slice(0, 10);

export default function TenantsPage() {
  const { user } = useAuth();
  const { showError, showSuccess } = useToast();
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [availableApplications, setAvailableApplications] = useState<Application[]>([]);
  const [selectedDealer, setSelectedDealer] = useState<string>('');
  const [form, setForm] = useState<TenantFormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<'create' | 'edit'>('create');
  const [editingTenantId, setEditingTenantId] = useState<string | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [revokingTenant, setRevokingTenant] = useState<Tenant | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);
  const [reactivatingTenant, setReactivatingTenant] = useState<Tenant | null>(null);
  const { t } = useTranslation();
  const isMaster = user?.role === 'master_full';
  const isDealer = user?.role === 'dealer_admin';
  const isTenantUser = user?.role === 'tenant_admin' || user?.role === 'tenant_user';
  const { page, setPage, rowsPerPage, setRowsPerPage, paginateData, resetPage } = usePagination(10);
  const [activeTab, setActiveTab] = useState<'active' | 'archived'>('active');

  const filteredTenants = useMemo(() => {
    return tenants.filter((tenant) =>
      activeTab === 'active' ? tenant.status === 'active' : tenant.status !== 'active',
    );
  }, [tenants, activeTab]);

  const columns = [
    { id: 'name', label: t('tenants.table.name') },
    { id: 'code', label: t('tenants.table.code') },
    { id: 'dealerName', label: t('tenants.table.dealer') },
    { id: 'locale', label: t('tenants.table.locale') },
    { id: 'status', label: t('tenants.table.status') },
    { id: 'isDemo', label: t('tenants.table.demo') },
    { id: 'licenseValidUntil', label: t('tenants.table.licenseValidUntil') },
    { id: 'maxPeople', label: t('tenants.table.maxPeople') },
    { id: 'maxDevices', label: t('tenants.table.maxDevices') },
    { id: 'applications', label: t('tenants.table.applications') },
    { id: 'modules', label: t('tenants.table.modules') },
    { id: 'createdAt', label: t('tenants.table.createdAt') },
    { id: 'actions', label: t('tenants.table.actions') },
  ];

  const [visibleColumns, setVisibleColumns] = usePersistedColumns('tenants-columns', [
    'name',
    'code',
    'dealerName',
    'locale',
    'status',
    'isDemo',
    'licenseValidUntil',
    'maxPeople',
    'maxDevices',
    'applications',
    'modules',
    'createdAt',
    'actions',
  ]);

  const { orderBy, order, handleRequestSort, sortedData } = useSortableTable(filteredTenants);

  // Revocation Details State
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [revocationDetails, setRevocationDetails] = useState<{
    revokedAt: string;
    revokedByUsername: string;
    reason: string | null;
  } | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleShowRevocationDetails = async (tenantId: string) => {
    setBusy(true);
    try {
      // Fetch revocation details from dedicated endpoint
      const details = await api.getTenantRevocationDetails(null!, tenantId);

      if (details) {
        setRevocationDetails(details);
        setDetailsDialogOpen(true);
      } else {
        showError(t('tenants.errors.noRevocationDetails', 'Details not found'));
      }
    } catch (e) {
      showError(getApiErrorMessage(e, t, 'tenants.errors.loadDetails'));
    } finally {
      setBusy(false);
    }
  };

  const handleReactivate = async () => {
    if (!reactivatingTenant) return;
    try {
      setBusy(true);
      await api.reactivateTenant(null, reactivatingTenant.id);
      showSuccess(t('tenants.messages.reactivated', 'Tenant reactivated successfully'));
      loadTenants(user?.role === 'dealer_admin' ? user.dealerId || undefined : undefined);
      setReactivateDialogOpen(false);
      setReactivatingTenant(null);
    } catch (e) {
      showError(getApiErrorMessage(e, t, 'tenants.errors.reactivate'));
    } finally {
      setBusy(false);
    }
  };

  const [manageAppsOpen, setManageAppsOpen] = useState(false);
  const [manageAppsForm, setManageAppsForm] = useState<{
    tenantId: string;
    dealerId: string;
    name: string;
    status: 'active' | 'inactive';
    licenseValidUntil: string;
    maxPeople: number;
    maxDevices: number;
    applicationIds: string[];
    applicationModules: Record<string, string[]>;
    moduleExpirations: Record<string, string>;
    isDemo: boolean;
    createdAt: string;
  } | null>(null);
  const [bulkDate, setBulkDate] = useState('');
  const [moduleSearch, setModuleSearch] = useState('');

  const handleAppsSubmit = async () => {
    if (!manageAppsForm) return;

    // VALIDATION: Strict Expiration
    const allSelectedModules = Object.values(manageAppsForm.applicationModules).flat();
    const missingExpiry = allSelectedModules.some((mid) => !manageAppsForm.moduleExpirations[mid]);
    if (missingExpiry) {
      showError(
        t('tenants.errors.missingExpiry', 'Expiration date is required for all selected modules.'),
      );
      return;
    }

    setBusy(true);
    try {
      const payload: UpdateTenantPayload = {
        name: manageAppsForm.name,
        status: manageAppsForm.status,
        licenseValidUntil: manageAppsForm.licenseValidUntil,
        maxPeople: manageAppsForm.maxPeople,
        maxDevices: manageAppsForm.maxDevices,
        applicationIds: manageAppsForm.applicationIds,
        applicationModules: manageAppsForm.applicationModules,
        moduleExpirations: manageAppsForm.moduleExpirations,
        isDemo: manageAppsForm.isDemo,
      };
      await api.updateTenant(null, manageAppsForm.tenantId, payload);
      showSuccess(t('tenants.messages.updated'));
      setManageAppsOpen(false);
      setManageAppsForm(null);
      const dealerId = user?.role === 'dealer_admin' ? (user.dealerId ?? '') : selectedDealer;
      await loadTenants(dealerId || undefined);
    } catch (err) {
      showError(getApiErrorMessage(err, t, 'tenants.errors.update'));
    } finally {
      setBusy(false);
    }
  };

  const [wizardForm, setWizardForm] = useState<TenantFormState>({
    dealerId: '',
    name: '',
    code: '',
    locale: DEFAULT_LOCALE,
    status: 'active',
    masterUsername: '',
    masterEmail: '',
    masterDisplayName: '',
    licenseValidUntil: '',
    maxPeople: '5',
    maxDevices: '5',
    isDemo: false,
    createdAt: new Date().toISOString(),
    applicationIds: [],
    applicationModules: {},
    moduleExpirations: {},
    logoData: undefined,
    logoPreview: null,
    phone: '+55',
    contactEmail: '',
  });

  const computeDefaultLicenseDate = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1);
    return date.toISOString().slice(0, 10);
  };

  const scopedDealers = useMemo(() => {
    if (!user) return [];
    if (user.role === 'dealer_admin' && user.dealerId) {
      return dealers.filter((d) => d.id === user.dealerId);
    }
    return dealers;
  }, [dealers, user]);

  const sortedApplications = useMemo(
    () => [...availableApplications].sort((a, b) => a.name.localeCompare(b.name)),
    [availableApplications],
  );

  const selectedApplications = useMemo(() => {
    const ids = wizardOpen ? wizardForm.applicationIds : (form?.applicationIds ?? []);
    if (ids.length === 0) return [] as Application[];
    const selectedIds = new Set(ids);
    return sortedApplications.filter((app) => selectedIds.has(app.id));
  }, [sortedApplications, form?.applicationIds, wizardForm.applicationIds, wizardOpen]);

  const loadDealers = async () => {
    try {
      const data = await api.listDealers(null);
      setDealers(data);
    } catch (err) {
      console.warn('Failed to load dealers', err);
    }
  };

  const loadApplicationsForDealer = async (dealerId?: string | null) => {
    if (!dealerId) {
      setAvailableApplications([]);
      setForm((prev) => (prev ? { ...prev, applicationIds: [], applicationModules: {} } : prev));
      return;
    }
    try {
      const data = await api.listApplications(null, {
        dealerId,
        includeModules: true,
      });
      setAvailableApplications(data);
      setForm((prev) => {
        if (!prev) return prev;
        const allowedApplications = new Set(data.map((app) => app.id));
        const filteredApplications = prev.applicationIds.filter((id) =>
          allowedApplications.has(id),
        );
        const moduleAllowMap = new Map(
          data.map((app) => [app.id, new Set((app.modules ?? []).map((module) => module.id))]),
        );
        const nextModules: Record<string, string[]> = {};
        for (const [appId, modules] of Object.entries(prev.applicationModules ?? {})) {
          if (!allowedApplications.has(appId)) {
            continue;
          }
          const allowedModuleSet = moduleAllowMap.get(appId) ?? new Set<string>();
          const filteredModules = modules.filter((moduleId) => allowedModuleSet.has(moduleId));
          if (filteredModules.length > 0) {
            nextModules[appId] = filteredModules;
          }
        }
        if (
          filteredApplications.length === prev.applicationIds.length &&
          Object.keys(nextModules).length === Object.keys(prev.applicationModules ?? {}).length
        ) {
          return prev;
        }
        return {
          ...prev,
          applicationIds: filteredApplications,
          applicationModules: nextModules,
        };
      });
    } catch (err) {
      console.warn('Failed to load applications for dealer', err);
      setAvailableApplications([]);
      setForm((prev) => (prev ? { ...prev, applicationIds: [], applicationModules: {} } : prev));
    }
  };

  const loadTenants = async (dealerId?: string) => {
    setLoading(true);
    try {
      const data = await api.listTenants(null, dealerId, { includeDetails: true });
      setTenants(data);
    } catch (err) {
      showError(getApiErrorMessage(err, t, 'tenants.errors.load'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDealers();
  }, []);

  useEffect(() => {
    const dealerId = user?.role === 'dealer_admin' ? (user.dealerId ?? '') : selectedDealer;
    loadTenants(dealerId || undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDealer, user?.role, user?.dealerId]);

  const handleRevoke = async () => {
    if (!revokingTenant) return;
    setBusy(true);
    try {
      await api.revokeTenant(null, revokingTenant.id, revokeReason.trim() || undefined);
      showSuccess(t('tenants.messages.revoked'));
      setRevokeDialogOpen(false);
      setRevokingTenant(null);
      setRevokeReason('');
      const dealerId = user?.role === 'dealer_admin' ? (user.dealerId ?? '') : selectedDealer;
      await loadTenants(dealerId || undefined);
    } catch (err) {
      showError(getApiErrorMessage(err, t, 'tenants.errors.revoke'));
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form) {
      return;
    }

    const licenseValidUntil = formatDateForInput(form.licenseValidUntil);
    if (!licenseValidUntil) {
      showError(t('tenants.errors.missingLicense'));
      return;
    }

    const maxPeople = Number(form.maxPeople);
    const maxDevices = Number(form.maxDevices);
    if (
      !Number.isFinite(maxPeople) ||
      maxPeople < 1 ||
      !Number.isFinite(maxDevices) ||
      maxDevices < 1
    ) {
      showError(t('tenants.errors.invalidLimits'));
      return;
    }

    const applicationIds = Array.from(new Set(form.applicationIds));
    const normalizedApplicationModules = Object.fromEntries(
      Object.entries(form.applicationModules ?? {}).map(([appId, modules]) => [
        appId,
        Array.from(new Set(modules)).filter(Boolean),
      ]),
    );
    const mergedApplicationIds = Array.from(
      new Set([...applicationIds, ...Object.keys(normalizedApplicationModules)]),
    );

    const basePayload = {
      dealerId: form.dealerId,
      name: form.name.trim(),
      code: form.code.trim(),
      status: form.status ?? 'active',
      locale: form.locale,
      licenseValidUntil,
      maxPeople,
      maxDevices,
      isDemo: form.isDemo,
      applicationIds: mergedApplicationIds,
      applicationModules: normalizedApplicationModules,
      ...(typeof form.logoData !== 'undefined' ? { logoData: form.logoData } : {}),
      phone: form.phone,
      contactEmail: form.contactEmail,
    };

    if (!basePayload.name || !basePayload.code) {
      showError(t('tenants.errors.missingRequired'));
      return;
    }

    setBusy(true);
    try {
      if (mode === 'create') {
        const masterUser = {
          username: form.masterUsername.trim(),
          email: form.masterEmail.trim(),
          displayName: form.masterDisplayName.trim(),
        };
        if (!masterUser.username || !masterUser.email) {
          showError(t('tenants.errors.missingRequired'));
          setBusy(false);
          return;
        }
        const payload: CreateTenantPayload = {
          dealerId: basePayload.dealerId,
          name: basePayload.name,
          code: basePayload.code,
          status: basePayload.status,
          locale: basePayload.locale,
          licenseValidUntil: basePayload.licenseValidUntil,
          maxPeople: basePayload.maxPeople,
          maxDevices: basePayload.maxDevices,
          isDemo: basePayload.isDemo,
          applicationIds: basePayload.applicationIds,
          applicationModules: basePayload.applicationModules,
          moduleExpirations: form.moduleExpirations,
          ...(typeof basePayload.logoData !== 'undefined'
            ? { logoData: basePayload.logoData }
            : {}),
          masterUser,
          phone: basePayload.phone,
          contactEmail: basePayload.contactEmail,
        };
        const result = await api.createTenant(null, payload);
        const initialPassword =
          (result.masterUser as { initialPassword?: string })?.initialPassword ?? '***';
        showSuccess(
          t('tenants.success', {
            username: result.masterUser.username,
            password: initialPassword,
          }),
        );
      } else if (mode === 'edit' && editingTenantId) {
        const payload: UpdateTenantPayload = {
          name: basePayload.name,
          status: basePayload.status,
          locale: basePayload.locale,
          licenseValidUntil: basePayload.licenseValidUntil,
          maxPeople: basePayload.maxPeople,
          maxDevices: basePayload.maxDevices,
          isDemo: basePayload.isDemo,
          applicationIds: basePayload.applicationIds,
          applicationModules: basePayload.applicationModules,
          moduleExpirations: form.moduleExpirations,
          ...(typeof basePayload.logoData !== 'undefined'
            ? { logoData: basePayload.logoData }
            : {}),
          phone: basePayload.phone,
          contactEmail: basePayload.contactEmail,
        };
        await api.updateTenant(null, editingTenantId, payload);
        showSuccess(t('tenants.messages.updated'));
      }

      setForm(null);
      setMode('create');
      setEditingTenantId(null);
      const dealerId = user?.role === 'dealer_admin' ? (user.dealerId ?? '') : selectedDealer;
      await loadTenants(dealerId || undefined);
    } catch (err) {
      const messageKey = mode === 'create' ? 'tenants.errors.create' : 'tenants.errors.update';
      showError(getApiErrorMessage(err, t, messageKey));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="flex-end"
        alignItems="center"
        spacing={2}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 200 }} disabled={user?.role === 'dealer_admin'}>
            <InputLabel id="dealer-filter-label">{t('tenants.form.dealer')}</InputLabel>
            <Select
              labelId="dealer-filter-label"
              label={t('tenants.form.dealer')}
              value={user?.role === 'dealer_admin' ? (user.dealerId ?? '') : selectedDealer}
              onChange={(event) => setSelectedDealer(event.target.value)}
            >
              <MenuItem value="">
                <em>{t('common.all')}</em>
              </MenuItem>
              {dealers.map((dealer) => (
                <MenuItem key={dealer.id} value={dealer.id}>
                  {dealer.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <ExportMenu
            data={sortedData}
            filename="tenants-export"
            columns={[
              { id: 'name', label: t('tenants.table.name') },
              { id: 'code', label: t('tenants.table.code') },
              {
                id: 'dealerId',
                label: t('tenants.table.dealer'),
                transform: (id) => dealers.find((d) => d.id === id)?.name || id || '—',
              },
              { id: 'locale', label: t('tenants.table.locale') },
              {
                id: 'status',
                label: t('tenants.table.status'),
                transform: (val) =>
                  val === 'active' ? 'Ativo' : val === 'inactive' ? 'Inativo' : val || '—',
              },
              {
                id: 'licenseValidUntil',
                label: t('tenants.table.licenseValidUntil'),
                transform: (val) => (val ? new Date(val).toLocaleDateString() : '—'),
              },
              { id: 'maxPeople', label: t('tenants.table.maxPeople') },
              { id: 'maxDevices', label: t('tenants.table.maxDevices') },
              {
                id: 'createdAt',
                label: t('tenants.table.createdAt'),
                transform: (val) => (val ? new Date(val).toLocaleDateString() : '—'),
              },
            ]}
          />
          {!isTenantUser && (
            <CustomButton
              variant="primary"
              onClick={() => {
                const initialDealerId = user?.dealerId ?? scopedDealers[0]?.id ?? '';
                const initialLocale =
                  scopedDealers.find((d) => d.id === initialDealerId)?.defaultLocale ??
                  DEFAULT_LOCALE;
                const initialLicense = computeDefaultLicenseDate();

                setWizardForm({
                  dealerId: initialDealerId,
                  name: '',
                  code: '',
                  locale: initialLocale,
                  status: 'active',
                  masterUsername: '',
                  masterEmail: '',
                  masterDisplayName: '',
                  licenseValidUntil: initialLicense,
                  maxPeople: '5',
                  maxDevices: '5',
                  isDemo: false,
                  createdAt: new Date().toISOString(),
                  applicationIds: [],
                  applicationModules: {},
                  moduleExpirations: {},
                  logoData: undefined,
                  logoPreview: null,
                  phone: '+55',
                  contactEmail: '',
                });
                loadApplicationsForDealer(initialDealerId);
                setWizardOpen(true);
              }}
              disabled={scopedDealers.length === 0}
            >
              {t('tenants.new')}
            </CustomButton>
          )}
          <Tooltip title={t('common.refresh')}>
            <span>
              <CustomButton
                variant="icon"
                icon={<RefreshIcon />}
                iconPosition="only"
                customSize={{ width: 36, height: 36 }}
                onClick={() => {
                  const dealerId =
                    user?.role === 'dealer_admin' ? (user.dealerId ?? '') : selectedDealer;
                  loadTenants(dealerId || undefined);
                }}
                disabled={loading}
              />
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Validation Logic */}
      {(() => {
        const identityValidation = createTenantSchema
          .pick({
            name: true,
            code: true,
            masterUsername: true,
            masterEmail: true,
            masterDisplayName: true,
          })
          .safeParse({
            name: wizardForm.name,
            code: wizardForm.code,
            masterUsername: wizardForm.masterUsername,
            masterEmail: wizardForm.masterEmail,
            masterDisplayName: wizardForm.masterDisplayName,
          });

        const licenseValidation = createTenantSchema
          .pick({
            licenseValidUntil: true,
            maxPeople: true,
            maxDevices: true,
          })
          .safeParse({
            licenseValidUntil: wizardForm.licenseValidUntil,
            maxPeople: wizardForm.maxPeople,
            maxDevices: wizardForm.maxDevices,
          });

        const identityErrors = identityValidation.success ? {} : identityValidation.error.flatten().fieldErrors;
        const licenseErrors = licenseValidation.success ? {} : licenseValidation.error.flatten().fieldErrors;

        return (
          <OnboardingWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        title={t('tenants.dialogTitle')}
        loading={busy}
        onFinish={async () => {
          // Trigger same logic as handleSubmit but using wizardForm
          setBusy(true);
          try {
            const payload: CreateTenantPayload = {
              dealerId: wizardForm.dealerId,
              name: wizardForm.name.trim(),
              code: wizardForm.code.trim(),
              status: wizardForm.status,
              locale: wizardForm.locale,
              licenseValidUntil: wizardForm.licenseValidUntil,
              maxPeople: Number(wizardForm.maxPeople),
              maxDevices: Number(wizardForm.maxDevices),
              isDemo: wizardForm.isDemo,
              applicationIds: wizardForm.applicationIds,
              applicationModules: wizardForm.applicationModules,
              moduleExpirations: wizardForm.moduleExpirations,
              masterUser: {
                username: wizardForm.masterUsername.trim(),
                email: wizardForm.masterEmail.trim(),
                displayName: wizardForm.masterDisplayName.trim(),
              },
              ...(typeof wizardForm.logoData !== 'undefined'
                ? { logoData: wizardForm.logoData }
                : {}),
              phone: wizardForm.phone,
              contactEmail: wizardForm.contactEmail || wizardForm.masterEmail.trim(),
            };
            const result = await api.createTenant(null, payload);
            const initialPassword = (result.masterUser as { initialPassword?: string }).initialPassword ?? '***';
            showSuccess(
              t('tenants.success', {
                username: result.masterUser.username,
                password: initialPassword,
              }),
            );
            setWizardOpen(false);
            await loadTenants((user?.dealerId ?? selectedDealer) || undefined);
          } catch (err) {
            showError(getApiErrorMessage(err, t, 'tenants.errors.create'));
          } finally {
            setBusy(false);
          }
        }}
        steps={[
          {
            label: 'tenants.wizard.identity',
            isValid: Boolean(wizardForm.dealerId) && identityValidation.success,
            content: (
              <Stack spacing={3}>
                <Typography variant="body2" color="text.secondary">
                  {t(
                    'tenants.wizard.identityDesc',
                    'Defina a identidade do tenant e os dados do administrador principal.',
                  )}
                </Typography>

                <Stack spacing={2} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                  <Typography variant="subtitle2" color="primary">
                    {t('tenants.wizard.tenantData', 'Dados do Tenant')}
                  </Typography>
                  <FormControl fullWidth required error={!wizardForm.dealerId}>
                    <InputLabel>{t('tenants.form.dealer')}</InputLabel>
                    <Select
                      value={wizardForm.dealerId}
                      label={t('tenants.form.dealer')}
                      onChange={(e) => {
                        const dId = e.target.value as string;
                        const d = scopedDealers.find((dl) => dl.id === dId);
                        setWizardForm((p) => ({
                          ...p,
                          dealerId: dId,
                          locale: d?.defaultLocale ?? DEFAULT_LOCALE,
                        }));
                        loadApplicationsForDealer(dId);
                      }}
                      disabled={user?.role === 'dealer_admin'}
                    >
                      {scopedDealers.map((d) => (
                        <MenuItem key={d.id} value={d.id}>
                          {d.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {!wizardForm.dealerId && <FormHelperText>{t('validation.required', 'Obrigatório')}</FormHelperText>}
                  </FormControl>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      fullWidth
                      label={t('tenants.form.name')}
                      value={wizardForm.name}
                      onChange={(e) => setWizardForm((p) => ({ ...p, name: e.target.value }))}
                      required
                      error={!!identityErrors.name}
                      helperText={identityErrors.name?.[0] ? t(identityErrors.name[0]) : ''}
                    />
                    <TextField
                      fullWidth
                      label={t('tenants.form.code')}
                      value={wizardForm.code}
                      onChange={(e) => setWizardForm((p) => ({ ...p, code: e.target.value }))}
                      required
                      error={!!identityErrors.code}
                      helperText={identityErrors.code?.[0] ? t(identityErrors.code[0]) : t('tenants.form.codeHelper')}
                    />
                  </Stack>
                </Stack>

                <Stack spacing={2} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                  <Typography variant="subtitle2" color="primary">
                    {t('tenants.wizard.adminData', 'Administrador e Contato')}
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      fullWidth
                      label={t('tenants.form.masterDisplayName')}
                      value={wizardForm.masterDisplayName}
                      onChange={(e) =>
                        setWizardForm((p) => ({ ...p, masterDisplayName: e.target.value }))
                      }
                      required
                      error={!!identityErrors.masterDisplayName}
                      helperText={identityErrors.masterDisplayName?.[0] ? t(identityErrors.masterDisplayName[0]) : ''}
                    />
                    <MuiTelInput
                      fullWidth
                      label={t('tenants.form.phoneNumber')}
                      value={wizardForm.phone}
                      onChange={(value) => setWizardForm((p) => ({ ...p, phone: value }))}
                      defaultCountry="BR"
                      preferredCountries={['BR', 'US', 'PT', 'ES', 'FR', 'GB', 'DE', 'IT']}
                    />
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      fullWidth
                      label={t('tenants.form.masterEmail')}
                      type="email"
                      value={wizardForm.masterEmail}
                      onChange={(e) =>
                        setWizardForm((p) => ({
                          ...p,
                          masterEmail: e.target.value,
                          contactEmail: e.target.value,
                        }))
                      }
                      required
                      error={!!identityErrors.masterEmail}
                      helperText={identityErrors.masterEmail?.[0] ? t(identityErrors.masterEmail[0]) : ''}
                    />
                    <TextField
                      fullWidth
                      label={t('tenants.form.masterUsername')}
                      value={wizardForm.masterUsername}
                      onChange={(e) =>
                        setWizardForm((p) => ({ ...p, masterUsername: e.target.value }))
                      }
                      required
                      error={!!identityErrors.masterUsername}
                      helperText={identityErrors.masterUsername?.[0] ? t(identityErrors.masterUsername[0]) : ''}
                    />
                  </Stack>
                </Stack>
              </Stack>
            ),
          },
          {
            label: 'tenants.wizard.license',
            isValid: licenseValidation.success,
            content: (
              <Stack spacing={3}>
                <Typography variant="body2" color="text.secondary">
                  {t(
                    'tenants.wizard.licenseDesc',
                    'Configure o status da licença e os limites operacionais.',
                  )}
                </Typography>

                <Stack spacing={2} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                  <Typography variant="subtitle2" color="primary">
                    {t('tenants.wizard.licenseStatus', 'Status da Licença')}
                  </Typography>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    sx={{
                      p: 1.5,
                      border: 1,
                      borderColor: wizardForm.isDemo ? 'primary.main' : 'divider',
                      bgcolor: wizardForm.isDemo ? 'action.selected' : 'transparent',
                      borderRadius: 1,
                      transition: 'all 0.2s',
                    }}
                  >
                    <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography
                        variant="body2"
                        fontWeight="medium"
                        color={wizardForm.isDemo ? 'primary.main' : 'text.primary'}
                      >
                        {t('tenants.form.isDemo', 'Conta de Demonstração (Trial)')}
                      </Typography>
                      <Tooltip
                        title={t('tenants.form.isDemoHelper')}
                        arrow
                        placement="top"
                      >
                        <InfoIcon fontSize="small" color="action" sx={{ fontSize: 18, cursor: 'help' }} />
                      </Tooltip>
                    </Box>
                    <Switch
                      checked={wizardForm.isDemo}
                      onChange={(e) => {
                        const demo = e.target.checked;
                        setWizardForm((p) => ({
                          ...p,
                          isDemo: demo,
                          licenseValidUntil: demo
                            ? computeDemoLicenseFromDate(p.createdAt)
                            : p.licenseValidUntil,
                        }));
                      }}
                    />
                  </Stack>

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <FormControl fullWidth>
                      <InputLabel>{t('tenants.form.status')}</InputLabel>
                      <Select
                        value={wizardForm.status}
                        label={t('tenants.form.status')}
                        onChange={(e) =>
                          setWizardForm((p) => ({
                            ...p,
                            status: e.target.value as 'active' | 'inactive',
                          }))
                        }
                      >
                        <MenuItem value="active">{t('tenants.status.active')}</MenuItem>
                        <MenuItem value="inactive">{t('tenants.status.inactive')}</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      type="date"
                      label={t('tenants.form.licenseValidUntil')}
                      InputLabelProps={{ shrink: true }}
                      value={wizardForm.licenseValidUntil}
                      onChange={(e) =>
                        setWizardForm((p) => ({
                          ...p,
                          licenseValidUntil: e.target.value,
                        }))
                      }
                      required
                      error={!!licenseErrors.licenseValidUntil}
                      helperText={licenseErrors.licenseValidUntil?.[0] ? t(licenseErrors.licenseValidUntil[0]) : ''}
                    />
                  </Stack>
                </Stack>

                <Stack spacing={2} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                  <Typography variant="subtitle2" color="primary">
                    {t('tenants.wizard.operationalLimits', 'Limites Operacionais')}
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      fullWidth
                      type="number"
                      label={t('tenants.form.maxPeople')}
                      value={wizardForm.maxPeople}
                      onChange={(e) =>
                        setWizardForm((p) => ({
                          ...p,
                          maxPeople: e.target.value,
                        }))
                      }
                      error={!!licenseErrors.maxPeople}
                      helperText={licenseErrors.maxPeople?.[0] ? t(licenseErrors.maxPeople[0]) : t('tenants.form.maxPeopleHelper', 'Deixe em branco para ilimitado')}
                    />
                    <TextField
                      fullWidth
                      type="number"
                      label={t('tenants.form.maxDevices')}
                      value={wizardForm.maxDevices}
                      onChange={(e) =>
                        setWizardForm((p) => ({
                          ...p,
                          maxDevices: e.target.value,
                        }))
                      }
                      error={!!licenseErrors.maxDevices}
                      helperText={licenseErrors.maxDevices?.[0] ? t(licenseErrors.maxDevices[0]) : t('tenants.form.maxDevicesHelper', 'Deixe em branco para ilimitado')}
                    />
                  </Stack>
                </Stack>
              </Stack>
            ),
          },
          {
            label: 'tenants.wizard.apps',
            isValid: manageAppsSchema.safeParse({
              applicationIds: wizardForm.applicationIds,
              applicationModules: wizardForm.applicationModules,
              moduleExpirations: wizardForm.moduleExpirations,
            }).success,
            content: (
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  {t(
                    'tenants.wizard.appsDesc',
                    'Selecione os aplicativos e módulos que este tenant terá acesso.',
                  )}
                </Typography>
                <FormControl fullWidth>
                  <InputLabel>{t('tenants.form.applications')}</InputLabel>
                  <Select
                    multiple
                    value={wizardForm.applicationIds}
                    onChange={(e) => {
                      const nextIds =
                        typeof e.target.value === 'string'
                          ? e.target.value.split(',')
                          : (e.target.value as string[]);
                      const allowedSet = new Set(nextIds);
                      const nextModules = Object.fromEntries(
                        Object.entries(wizardForm.applicationModules).filter(([id]) =>
                          allowedSet.has(id),
                        ),
                      );
                      setWizardForm((p) => ({
                        ...p,
                        applicationIds: nextIds,
                        applicationModules: nextModules,
                      }));
                    }}
                    label={t('tenants.form.applications')}
                    renderValue={(sel) =>
                      sel
                        .map((id) => sortedApplications.find((a) => a.id === id)?.name || id)
                        .join(', ')
                    }
                  >
                    {sortedApplications.map((app) => (
                      <MenuItem key={app.id} value={app.id}>
                        <Checkbox checked={wizardForm.applicationIds.includes(app.id)} />
                        <ListItemText primary={app.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Bulk Date Controls */}
                {Object.values(wizardForm.applicationModules).flat().length > 0 && (
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    sx={{
                      p: 1.5,
                      bgcolor: wizardForm.isDemo ? 'info.main' : 'action.hover',
                      color: wizardForm.isDemo ? 'info.contrastText' : 'text.primary',
                      borderRadius: 1,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {wizardForm.isDemo
                          ? t('tenants.wizard.demoModeActive', 'Modo Demo Ativo')
                          : t('tenants.wizard.bulkDateControl', 'Controle de Datas em Lote')}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        {wizardForm.isDemo
                          ? t('tenants.wizard.demoModeDesc', {
                              defaultValue: `Todos os módulos expiram em ${CONFIG.DEMO_DURATION_DAYS} dias`,
                              count: CONFIG.DEMO_DURATION_DAYS,
                            })
                          : t(
                              'tenants.wizard.bulkDateDesc',
                              'Aplique uma data para todos os módulos selecionados',
                            )}
                      </Typography>
                    </Box>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      alignItems={{ xs: 'stretch', sm: 'center' }}
                      sx={{ width: { xs: '100%', sm: 'auto' } }}
                    >
                      {wizardForm.isDemo ? (
                        <CustomButton
                          variant="secondary"
                          aria-label={t(
                            'tenants.a11y.selectAllModules',
                            'Selecionar todos os módulos de todos os aplicativos',
                          )}
                          onClick={() => {
                            const demoDate = computeDemoLicenseFromDate(wizardForm.createdAt);
                            const allModuleIds = Object.values(wizardForm.applicationModules).flat();
                            const newExpirations: Record<string, string> = {};
                            allModuleIds.forEach((mid) => {
                              newExpirations[mid] = demoDate;
                            });
                            setWizardForm((p) => ({
                              ...p,
                              moduleExpirations: newExpirations,
                            }));
                          }}
                        >
                          {t('tenants.wizard.applyDemoDate', {
                            defaultValue: `Demo (${CONFIG.DEMO_DURATION_DAYS}d)`,
                            count: CONFIG.DEMO_DURATION_DAYS,
                          })}
                        </CustomButton>
                      ) : (
                        <>
                          <TextField
                            type="date"
                            size="small"
                            value={bulkDate}
                            onChange={(e) => setBulkDate(e.target.value)}
                            sx={{ minWidth: { sm: 150 } }}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                              'aria-label': t('tenants.a11y.bulkDateInput', 'Data para aplicar em lote'),
                            }}
                          />
                          <CustomButton
                            variant="primary"
                            disabled={!bulkDate}
                            onClick={() => {
                              const allModuleIds = Object.values(wizardForm.applicationModules).flat();
                              const newExpirations: Record<string, string> = { ...wizardForm.moduleExpirations };
                              allModuleIds.forEach((mid) => {
                                newExpirations[mid] = bulkDate;
                              });
                              setWizardForm((p) => ({
                                ...p,
                                moduleExpirations: newExpirations,
                              }));
                            }}
                          >
                            {t('common.apply', 'Aplicar')}
                          </CustomButton>
                        </>
                      )}
                    </Stack>
                  </Stack>
                )}

                {/* Detailed Module Selection for Wizard with Expiration */}
                <Box
                  sx={{
                    maxHeight: 300,
                    overflowY: 'auto',
                    p: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  {selectedApplications.map((app) => {
                    const moduleOptions = app.modules ?? [];
                    const selectedModuleIds = wizardForm.applicationModules[app.id] ?? [];

                    return (
                      <Accordion
                        key={app.id}
                        disableGutters
                        elevation={0}
                        defaultExpanded
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          mb: 1,
                          '&:before': { display: 'none' },
                          borderRadius: 1,
                          overflow: 'hidden',
                        }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              width: '100%',
                              mr: 1,
                            }}
                          >
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="subtitle2" fontWeight={700}>
                                {app.name}
                              </Typography>
                              <CustomButton
                                variant="secondary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const allModuleIds = moduleOptions.map((m) => m.id);
                                  const defaultDate = wizardForm.isDemo
                                    ? computeDemoLicenseFromDate(wizardForm.createdAt)
                                    : wizardForm.licenseValidUntil;
                                  const newExpirations = {
                                    ...wizardForm.moduleExpirations,
                                  };
                                  allModuleIds.forEach((mid) => {
                                    if (!newExpirations[mid]) newExpirations[mid] = defaultDate;
                                  });
                                  setWizardForm((p) => ({
                                    ...p,
                                    applicationModules: {
                                      ...p.applicationModules,
                                      [app.id]: allModuleIds,
                                    },
                                    moduleExpirations: newExpirations,
                                  }));
                                }}
                              >
                                {t('common.selectAll', 'Todos')}
                              </CustomButton>
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                              {selectedModuleIds.length > 0
                                ? `(${selectedModuleIds.length} ${t(
                                    'tenants.form.modulesSelected',
                                    'módulos',
                                  )})`
                                : t('tenants.form.noModulesSelected', '(Nenhum)')}
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          {moduleOptions.length > 0 ? (
                            <Stack spacing={1}>
                              {moduleOptions.map((module) => {
                                const isChecked = selectedModuleIds.includes(module.id);
                                const moduleExpiry = wizardForm.moduleExpirations[module.id] || '';
                                const error = isChecked && !moduleExpiry;

                                return (
                                  <Stack
                                    key={module.id}
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    sx={{ py: 0.5 }}
                                  >
                                    <FormControlLabel
                                      control={
                                        <Checkbox
                                          size="small"
                                          checked={isChecked}
                                          onChange={(event) =>
                                            setWizardForm((prev) => {
                                              const current = new Set(
                                                prev.applicationModules[app.id] ?? [],
                                              );
                                              const nextExpirations = {
                                                ...prev.moduleExpirations,
                                              };

                                              if (event.target.checked) {
                                                current.add(module.id);
                                                if (!nextExpirations[module.id]) {
                                                  nextExpirations[module.id] = prev.isDemo
                                                    ? computeDemoLicenseFromDate(prev.createdAt)
                                                    : prev.licenseValidUntil;
                                                }
                                              } else {
                                                current.delete(module.id);
                                                delete nextExpirations[module.id];
                                              }

                                              return {
                                                ...prev,
                                                applicationModules: {
                                                  ...prev.applicationModules,
                                                  [app.id]: Array.from(current),
                                                },
                                                moduleExpirations: nextExpirations,
                                              };
                                            })
                                          }
                                        />
                                      }
                                      label={
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                          }}
                                        >
                                          {module.color && (
                                            <Box
                                              sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                bgcolor: module.color,
                                                border: `1px solid ${theme.palette.divider}`,
                                              }}
                                            />
                                          )}
                                          <Typography variant="body2">{module.name}</Typography>
                                        </Box>
                                      }
                                      sx={{ flex: 1, minWidth: 0, m: 0 }}
                                    />
                                    {isChecked && (
                                      <TextField
                                        error={Boolean(error)}
                                        type="date"
                                        size="small"
                                        label={t('tenants.form.expiresAt', 'Expira')}
                                        value={moduleExpiry}
                                        onChange={(event) =>
                                          setWizardForm((prev) => ({
                                            ...prev,
                                            moduleExpirations: {
                                              ...prev.moduleExpirations,
                                              [module.id]: event.target.value,
                                            },
                                          }))
                                        }
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ width: 150 }}
                                      />
                                    )}
                                  </Stack>
                                );
                              })}
                            </Stack>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              {t('tenants.form.modulesEmptyForApplication')}
                            </Typography>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                  {selectedApplications.length === 0 && (
                    <Box sx={{ py: 4, textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        {t(
                          'tenants.wizard.selectAppsFirst',
                          'Selecione aplicativos acima para ver os módulos disponíveis',
                        )}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Stack>
            ),
          },
        ]}
      />
      );
      })()}

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => {
              setActiveTab(newValue);
              resetPage();
            }}
            sx={{ px: 2 }}
          >
            <Tab
              value="active"
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <span>{t('tenants.tabs.active')}</span>
                  <Chip
                    label={tenants.filter((t) => t.status === 'active').length}
                    size="small"
                    color="success"
                  />
                </Stack>
              }
            />
            <Tab
              value="archived"
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <span>{t('tenants.tabs.archived')}</span>
                  <Chip
                    label={tenants.filter((t) => t.status !== 'active').length}
                    size="small"
                    color="default"
                  />
                </Stack>
              }
            />
          </Tabs>
        </Box>
        <CardContent>
          {(() => {
            if (loading) {
              return (
                <Typography variant="body2" color="text.secondary">
                  {t('tenants.loading')}
                </Typography>
              );
            }

            if (filteredTenants.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary">
                  {activeTab === 'active' ? t('tenants.empty') : t('tenants.emptyArchived')}
                </Typography>
              );
            }

            return (
              <>
                <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
                  <TableColumnSelector
                    columns={columns}
                    visibleColumns={visibleColumns}
                    onChange={(cols) => setVisibleColumns(cols as string[])}
                  />
                </Stack>
                <TableContainer component={Box} sx={{ overflowX: 'auto' }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        {visibleColumns.includes('name') && (
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === 'name'}
                              direction={orderBy === 'name' ? order : 'asc'}
                              onClick={() => handleRequestSort('name')}
                            >
                              {t('tenants.table.name')}
                            </TableSortLabel>
                          </TableCell>
                        )}
                        {visibleColumns.includes('code') && (
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === 'code'}
                              direction={orderBy === 'code' ? order : 'asc'}
                              onClick={() => handleRequestSort('code')}
                            >
                              {t('tenants.table.code')}
                            </TableSortLabel>
                          </TableCell>
                        )}
                        {visibleColumns.includes('dealerName') && (
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === 'dealerName'}
                              direction={orderBy === 'dealerName' ? order : 'asc'}
                              onClick={() => handleRequestSort('dealerName')}
                            >
                              {t('tenants.table.dealer')}
                            </TableSortLabel>
                          </TableCell>
                        )}
                        {visibleColumns.includes('locale') && (
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === 'locale'}
                              direction={orderBy === 'locale' ? order : 'asc'}
                              onClick={() => handleRequestSort('locale')}
                            >
                              {t('tenants.table.locale')}
                            </TableSortLabel>
                          </TableCell>
                        )}
                        {visibleColumns.includes('status') && (
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === 'status'}
                              direction={orderBy === 'status' ? order : 'asc'}
                              onClick={() => handleRequestSort('status')}
                            >
                              {t('tenants.table.status')}
                            </TableSortLabel>
                          </TableCell>
                        )}
                        {visibleColumns.includes('isDemo') && (
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === 'isDemo'}
                              direction={orderBy === 'isDemo' ? order : 'asc'}
                              onClick={() => handleRequestSort('isDemo')}
                            >
                              {t('tenants.table.demo')}
                            </TableSortLabel>
                          </TableCell>
                        )}
                        {visibleColumns.includes('licenseValidUntil') && (
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === 'licenseValidUntil'}
                              direction={orderBy === 'licenseValidUntil' ? order : 'asc'}
                              onClick={() => handleRequestSort('licenseValidUntil')}
                            >
                              {t('tenants.table.licenseValidUntil')}
                            </TableSortLabel>
                          </TableCell>
                        )}
                        {visibleColumns.includes('maxPeople') && (
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === 'maxPeople'}
                              direction={orderBy === 'maxPeople' ? order : 'asc'}
                              onClick={() => handleRequestSort('maxPeople')}
                            >
                              {t('tenants.table.maxPeople')}
                            </TableSortLabel>
                          </TableCell>
                        )}
                        {visibleColumns.includes('maxDevices') && (
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === 'maxDevices'}
                              direction={orderBy === 'maxDevices' ? order : 'asc'}
                              onClick={() => handleRequestSort('maxDevices')}
                            >
                              {t('tenants.table.maxDevices')}
                            </TableSortLabel>
                          </TableCell>
                        )}
                        {visibleColumns.includes('applications') && (
                          <TableCell>{t('tenants.table.applications')}</TableCell>
                        )}
                        {visibleColumns.includes('modules') && (
                          <TableCell>{t('tenants.table.modules')}</TableCell>
                        )}
                        {visibleColumns.includes('createdAt') && (
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === 'createdAt'}
                              direction={orderBy === 'createdAt' ? order : 'asc'}
                              onClick={() => handleRequestSort('createdAt')}
                            >
                              {t('tenants.table.createdAt')}
                            </TableSortLabel>
                          </TableCell>
                        )}
                        {visibleColumns.includes('actions') && (
                          <TableCell align="right">{t('tenants.table.actions')}</TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginateData(sortedData).map((tenant) => (
                        <TableRow key={tenant.id}>
                          {visibleColumns.includes('name') && (
                            <TableCell>
                              <Stack direction="row" spacing={1.5} alignItems="center">
                                {tenant.logoUrl && (
                                  <Avatar
                                    src={tenant.logoUrl}
                                    alt={tenant.name}
                                    variant="rounded"
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      bgcolor: 'grey.100',
                                    }}
                                  />
                                )}
                                <Typography>{tenant.name}</Typography>
                              </Stack>
                            </TableCell>
                          )}
                          {visibleColumns.includes('code') && (
                            <TableCell>
                              <Typography fontFamily="monospace">{tenant.code}</Typography>
                            </TableCell>
                          )}
                          {visibleColumns.includes('dealerName') && (
                            <TableCell>{tenant.dealerName ?? tenant.dealerId}</TableCell>
                          )}
                          {visibleColumns.includes('locale') && (
                            <TableCell>{tenant.locale}</TableCell>
                          )}
                          {visibleColumns.includes('status') && (
                            <TableCell>
                              <Chip
                                size="small"
                                label={t(`tenants.status.${tenant.status}`)}
                                color={tenant.status === 'active' ? 'success' : 'default'}
                              />
                            </TableCell>
                          )}
                          {visibleColumns.includes('isDemo') && (
                            <TableCell>
                              <Chip
                                size="small"
                                label={tenant.isDemo ? t('tenants.demo.yes') : t('tenants.demo.no')}
                                color={tenant.isDemo ? 'warning' : 'default'}
                              />
                            </TableCell>
                          )}
                          {visibleColumns.includes('licenseValidUntil') && (
                            <TableCell>{formatDateForDisplay(tenant.licenseValidUntil)}</TableCell>
                          )}
                          {visibleColumns.includes('maxPeople') && (
                            <TableCell>{tenant.maxPeople}</TableCell>
                          )}
                          {visibleColumns.includes('maxDevices') && (
                            <TableCell>{tenant.maxDevices}</TableCell>
                          )}
                          {visibleColumns.includes('applications') && (
                            <TableCell>
                              {tenant.applications.length > 0 ? (
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                  {tenant.applications.slice(0, 2).map((app) => (
                                    <Chip key={app.id} label={app.name} size="small" />
                                  ))}
                                  {tenant.applications.length > 2 && (
                                    <Chip
                                      label={`+${tenant.applications.length - 2}`}
                                      size="small"
                                      variant="outlined"
                                      sx={{ fontWeight: 600 }}
                                    />
                                  )}
                                </Stack>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  {t('tenants.table.applicationsEmpty')}
                                </Typography>
                              )}
                            </TableCell>
                          )}
                          {visibleColumns.includes('modules') && (
                            <TableCell>
                              {tenant.applications.some((app) => (app.modules ?? []).length > 0) ? (
                                (() => {
                                  const allModules = tenant.applications.flatMap((app) =>
                                    (app.modules ?? []).map((module) => ({
                                      ...module,
                                      appId: app.id,
                                    })),
                                  );
                                  return (
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                      {allModules.slice(0, 2).map((module) => (
                                        <Chip
                                          key={`${module.appId}-${module.id}`}
                                          label={module.name}
                                          size="small"
                                          variant={module.color ? 'filled' : 'outlined'}
                                          sx={
                                            module.color
                                              ? {
                                                  bgcolor: module.color,
                                                  color: 'white',
                                                  fontWeight: 500,
                                                  '& .MuiChip-label': { px: 1 },
                                                }
                                              : undefined
                                          }
                                        />
                                      ))}
                                      {allModules.length > 2 && (
                                        <Chip
                                          label={`+${allModules.length - 2}`}
                                          size="small"
                                          variant="outlined"
                                          sx={{ fontWeight: 600 }}
                                        />
                                      )}
                                    </Stack>
                                  );
                                })()
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  {t('tenants.table.modulesEmpty')}
                                </Typography>
                              )}
                            </TableCell>
                          )}
                          {visibleColumns.includes('createdAt') && (
                            <TableCell>{new Date(tenant.createdAt).toLocaleString()}</TableCell>
                          )}
                          {visibleColumns.includes('actions') && (
                            <TableCell align="right">
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                {tenant.status === 'active' ? (
                                  <>
                                    <Tooltip
                                      title={
                                        isTenantUser ? t('common.view') : t('tenants.actions.edit')
                                      }
                                    >
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => {
                                          setMode('edit');
                                          setEditingTenantId(tenant.id);
                                          loadApplicationsForDealer(tenant.dealerId);
                                          setForm({
                                            dealerId: tenant.dealerId,
                                            name: tenant.name,
                                            code: tenant.code,
                                            locale: tenant.locale,
                                            status: tenant.status,
                                            masterUsername: '',
                                            masterEmail: '',
                                            masterDisplayName: '',
                                            licenseValidUntil: formatDateForInput(
                                              tenant.licenseValidUntil,
                                            ),
                                            maxPeople: String(tenant.maxPeople),
                                            maxDevices: String(tenant.maxDevices),
                                            isDemo: tenant.isDemo,
                                            createdAt: tenant.createdAt,
                                            applicationIds: tenant.applications.map(
                                              (app) => app.id,
                                            ),
                                            applicationModules: Object.fromEntries(
                                              tenant.applications
                                                .map((app) => [
                                                  app.id,
                                                  app.modules?.map((module) => module.id) ?? [],
                                                ])
                                                .filter(([, modules]) => modules.length > 0),
                                            ),
                                            moduleExpirations: Object.fromEntries(
                                              tenant.applications
                                                .flatMap((app) =>
                                                  (app.modules || []).map((module) => [
                                                    module.id,
                                                    module.validUntil || '',
                                                  ]),
                                                )
                                                .filter(([, validUntil]) => validUntil),
                                            ),
                                            logoData: undefined,
                                            logoPreview: tenant.logoUrl ?? null,
                                            phone: tenant.phone ?? '+55',
                                            contactEmail: tenant.contactEmail ?? '',
                                          });
                                        }}
                                      >
                                        {isTenantUser ? (
                                          <VisibilityIcon fontSize="small" />
                                        ) : (
                                          <EditIcon fontSize="small" />
                                        )}
                                      </IconButton>
                                    </Tooltip>

                                    <Tooltip title={t('tenants.actions.manageApps', 'Manage Apps')}>
                                      <IconButton
                                        size="small"
                                        color="info"
                                        onClick={() => {
                                          loadApplicationsForDealer(tenant.dealerId);
                                          setManageAppsForm({
                                            tenantId: tenant.id,
                                            dealerId: tenant.dealerId,
                                            name: tenant.name,
                                            status: tenant.status,
                                            licenseValidUntil: tenant.licenseValidUntil,
                                            maxPeople: tenant.maxPeople,
                                            maxDevices: tenant.maxDevices,
                                            applicationIds: tenant.applications.map((a) => a.id),
                                            applicationModules: Object.fromEntries(
                                              tenant.applications.map((a) => [
                                                a.id,
                                                a.modules?.map((m) => m.id) ?? [],
                                              ]),
                                            ),
                                            moduleExpirations: Object.fromEntries(
                                              tenant.applications.flatMap((a) =>
                                                (a.modules ?? []).map((m) => [
                                                  m.id,
                                                  m.validUntil || '',
                                                ]),
                                              ),
                                            ),
                                            isDemo: tenant.isDemo,
                                            createdAt: tenant.createdAt,
                                          });
                                          setManageAppsOpen(true);
                                        }}
                                      >
                                        <SettingsIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>

                                    <Tooltip
                                      title={t('tenants.actions.exportLicense', 'Export License')}
                                    >
                                      <IconButton
                                        size="small"
                                        color="secondary"
                                        onClick={async () => {
                                          setBusy(true);
                                          try {
                                            const { blob, filename } =
                                              await api.exportTenantLicense(null, tenant.id);
                                            const url = URL.createObjectURL(blob);
                                            const a = document.createElement('a');
                                            a.href = url;
                                            a.download = filename;
                                            document.body.appendChild(a);
                                            a.click();
                                            document.body.removeChild(a);
                                            URL.revokeObjectURL(url);
                                            showSuccess(
                                              t(
                                                'tenants.messages.licenseExported',
                                                'License exported successfully',
                                              ),
                                            );
                                          } catch (e) {
                                            showError(
                                              getApiErrorMessage(
                                                e,
                                                t,
                                                'tenants.errors.exportLicense',
                                              ),
                                            );
                                          } finally {
                                            setBusy(false);
                                          }
                                        }}
                                      >
                                        <FileDownloadIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>

                                    {(isMaster || isDealer) && (
                                      <Tooltip title={t('tenants.actions.revoke')}>
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() => {
                                            setRevokingTenant(tenant);
                                            setRevokeReason('');
                                            setRevokeDialogOpen(true);
                                          }}
                                        >
                                          <BlockIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </>
                                ) : (
                                  (isMaster || isDealer) && (
                                    <Tooltip title={t('tenants.actions.reactivate', 'Reactivate')}>
                                      <IconButton
                                        size="small"
                                        color="success"
                                        onClick={() => {
                                          setReactivatingTenant(tenant);
                                          setReactivateDialogOpen(true);
                                        }}
                                      >
                                        <RestoreIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )
                                )}

                                {/* Details Button for Inactive Tenants */}
                                {tenant.status === 'inactive' && (
                                  <Tooltip title={t('tenants.actions.details', 'Details')}>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => handleShowRevocationDetails(tenant.id)}
                                    >
                                      <InfoIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Stack>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePaginationControl
                  count={sortedData.length}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onPageChange={setPage}
                  onRowsPerPageChange={setRowsPerPage}
                />
              </>
            );
          })()}
        </CardContent>
      </Card>

      <FormDialog
        open={Boolean(form)}
        onClose={() => {
          setForm(null);
          setMode('create');
          setEditingTenantId(null);
        }}
        maxWidth="md"
        loading={busy}
        disableBackdropClick
        title={mode === 'create' ? t('tenants.dialogTitle') : t('tenants.dialogEditTitle')}
        icon={mode === 'create' ? <BusinessIcon /> : <EditIcon />}
        formId="tenant-form"
        submitText={mode === 'create' ? t('tenants.dialogSubmit') : t('tenants.dialogSubmitEdit')}
        cancelText={isTenantUser ? t('common.close', 'Fechar') : t('common.cancel')}
        hideSubmit={isTenantUser}
        disableSubmit={
          mode === 'create'
            ? !createTenantSchema.safeParse(form).success
            : !updateTenantSchema.safeParse(form).success
        }
      >
          <Stack component="form" id="tenant-form" onSubmit={handleSubmit} sx={{ py: 1 }}>
            {/* Identity Section */}
            <Grid container spacing={2}>
              {/* Logo Column */}
              <Grid
                item
                xs={12}
                sm={3}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    aspectRatio: '1/1',
                    borderRadius: 2,
                    border: '1px dashed rgba(0,0,0,0.2)',
                    display: 'flex',
                    textAlign: 'center',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    bgcolor: 'background.default',
                    position: 'relative',
                  }}
                >
                  {form?.logoPreview ? (
                    <Box
                      component="img"
                      src={form.logoPreview}
                      alt="Logo"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        p: 1,
                      }}
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      {t('tenants.form.logoEmpty', 'No Logo')}
                    </Typography>
                  )}
                </Box>
                <label>
                  <CustomButton variant="outline" disabled={busy || isTenantUser} fullWidth>
                    {t('tenants.form.logoUpload', 'Upload')}
                  </CustomButton>
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file && form) {
                        const reader = new FileReader();
                        reader.onload = (rev) => {
                          if (typeof rev.target?.result === 'string') {
                            setForm({
                              ...form,
                              logoData: rev.target.result,
                              logoPreview: rev.target.result,
                            });
                          }
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                </label>
                {form?.logoPreview && (
                  <CustomButton
                    variant="secondary"
                    fullWidth
                    onClick={() =>
                      setForm((f) => (f ? { ...f, logoData: null, logoPreview: null } : f))
                    }
                    disabled={busy || isTenantUser}
                  >
                    {t('common.remove', 'Remove')}
                  </CustomButton>
                )}
              </Grid>

              {/* Main Fields Column */}
              <Grid item xs={12} sm={9}>
                <Grid container spacing={2}>
                  {/* Dealer (if applicable) */}
                  <Grid item xs={12} sm={6}>
                    <FormControl required fullWidth disabled={isTenantUser} size="medium">
                      <InputLabel>{t('tenants.form.dealer')}</InputLabel>
                      <Select
                        label={t('tenants.form.dealer')}
                        value={form?.dealerId ?? ''}
                        onChange={(e) => {
                          const dealerId = e.target.value as string;
                          const dealer = scopedDealers.find((d) => d.id === dealerId);
                          setForm((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  dealerId,
                                  locale: dealer?.defaultLocale ?? DEFAULT_LOCALE,
                                }
                              : prev,
                          );
                          loadApplicationsForDealer(dealerId);
                        }}
                        disabled={
                          busy || mode === 'edit' || user?.role === 'dealer_admin' || isTenantUser
                        }
                        startAdornment={
                          <InputAdornment position="start">
                            <StoreIcon sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        }
                      >
                        {scopedDealers.map((d) => (
                          <MenuItem key={d.id} value={d.id}>
                            {d.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  {/* Locale */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="medium">
                      <InputLabel>{t('tenants.form.locale')}</InputLabel>
                      <Select
                        label={t('tenants.form.locale')}
                        value={form?.locale ?? DEFAULT_LOCALE}
                        onChange={(e) =>
                          setForm((prev) =>
                            prev ? { ...prev, locale: e.target.value as string } : prev,
                          )
                        }
                        startAdornment={
                          <InputAdornment position="start">
                            <LanguageIcon sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        }
                      >
                        {LOCALE_OPTIONS.map((o) => (
                          <MenuItem key={o.value} value={o.value}>
                            {o.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Name */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t('tenants.form.name')}
                      value={form?.name ?? ''}
                      onChange={(e) =>
                        setForm((prev) => (prev ? { ...prev, name: e.target.value } : prev))
                      }
                      required
                      disabled={busy || isTenantUser}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BusinessIcon sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  {/* Code */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t('tenants.form.code')}
                      value={form?.code ?? ''}
                      onChange={(e) =>
                        setForm((prev) => (prev ? { ...prev, code: e.target.value } : prev))
                      }
                      required
                      disabled={busy || mode === 'edit' || isTenantUser}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <TagIcon sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }}>
              <Chip label={t('tenants.wizard.licensing')} size="small" />
            </Divider>

            {/* Licensing Section */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('tenants.form.licenseValidUntil')}
                  type="date"
                  value={form ? formatDateForInput(form.licenseValidUntil) : ''}
                  onChange={(e) =>
                    setForm((prev) =>
                      prev ? { ...prev, licenseValidUntil: e.target.value } : prev,
                    )
                  }
                  required
                  disabled={busy || (!isMaster && Boolean(form?.isDemo)) || isTenantUser}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarTodayIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Stack
                  direction="row"
                  alignItems="center"
                  component="label"
                  spacing={2}
                  sx={{
                    height: '100%',
                    border: 1,
                    borderColor: form?.isDemo ? 'primary.main' : 'divider',
                    bgcolor: form?.isDemo ? 'action.selected' : 'transparent',
                    borderRadius: 1,
                    p: 1.5,
                    cursor: (busy || isTenantUser) ? 'default' : 'pointer',
                    transition: 'all 0.2s',
                    opacity: (busy || isTenantUser) ? 0.7 : 1,
                    '&:hover': {
                      bgcolor: (busy || isTenantUser) ? (form?.isDemo ? 'action.selected' : 'transparent') : 'action.hover',
                    },
                  }}
                >
                  <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography 
                      variant="body2" 
                      fontWeight="medium"
                      color={form?.isDemo ? 'primary.main' : 'text.primary'}
                    >
                      {t('tenants.form.isDemo', 'Cliente de demonstração')}
                    </Typography>
                    <Tooltip
                      title={form?.isDemo && !isMaster ? t('tenants.form.isDemoInfo') : t('tenants.form.isDemoHelper')}
                      arrow
                      placement="top"
                    >
                      <InfoIcon fontSize="small" color="action" sx={{ fontSize: 18, cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                  <Switch
                    checked={Boolean(form?.isDemo)}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      const baseDate = form?.createdAt || new Date().toISOString();
                      const demoExpiry = computeDemoLicenseFromDate(baseDate);
                      // Logic for demo expiry
                      setForm((prev) => {
                        if (!prev) return prev;
                        const nextModuleExpirations = {
                          ...prev.moduleExpirations,
                        };
                        if (checked) {
                          Object.values(prev.applicationModules)
                            .flat()
                            .forEach((mid) => {
                              if (!nextModuleExpirations[mid])
                                nextModuleExpirations[mid] = demoExpiry;
                            });
                        }
                        return {
                          ...prev,
                          isDemo: checked,
                          licenseValidUntil: checked ? demoExpiry : prev.licenseValidUntil,
                          moduleExpirations: nextModuleExpirations,
                        };
                      });
                    }}
                    disabled={busy || isTenantUser}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('tenants.form.maxPeople')}
                  type="number"
                  value={form?.maxPeople ?? ''}
                  onChange={(e) =>
                    setForm((prev) => (prev ? { ...prev, maxPeople: e.target.value } : prev))
                  }
                  required
                  disabled={busy || isTenantUser}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PeopleIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('tenants.form.maxDevices')}
                  type="number"
                  value={form?.maxDevices ?? ''}
                  onChange={(e) =>
                    setForm((prev) => (prev ? { ...prev, maxDevices: e.target.value } : prev))
                  }
                  required
                  disabled={busy || isTenantUser}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DevicesIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>

            {/* Master User for Create Mode */}
            {mode === 'create' && (
              <>
                <Divider sx={{ my: 3 }}>
                  <Chip label={t('tenants.wizard.masterUser')} size="small" />
                </Divider>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('tenants.form.masterUsername')}
                      value={form?.masterUsername ?? ''}
                      onChange={(e) =>
                        setForm((prev) =>
                          prev ? { ...prev, masterUsername: e.target.value } : prev,
                        )
                      }
                      required
                      disabled={busy}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t('tenants.form.masterEmail')}
                      type="email"
                      value={form?.masterEmail ?? ''}
                      onChange={(e) =>
                        setForm((prev) => (prev ? { ...prev, masterEmail: e.target.value } : prev))
                      }
                      required
                      disabled={busy}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t('tenants.form.masterDisplayName')}
                      value={form?.masterDisplayName ?? ''}
                      onChange={(e) =>
                        setForm((prev) =>
                          prev ? { ...prev, masterDisplayName: e.target.value } : prev,
                        )
                      }
                      required
                      disabled={busy}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PeopleIcon sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </>
            )}

            <Divider sx={{ my: 3 }}>
              <Chip label={t('tenants.wizard.contact')} size="small" />
            </Divider>

            {/* Contact Section */}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('tenants.form.contactEmail')}
                  value={form?.contactEmail ?? ''}
                  onChange={(e) =>
                    setForm((prev) => (prev ? { ...prev, contactEmail: e.target.value } : prev))
                  }
                  disabled={busy || isTenantUser}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <MuiTelInput
                  fullWidth
                  label={t('tenants.form.phoneNumber')}
                  value={form?.phone ?? ''}
                  onChange={(value) => setForm((p) => (p ? { ...p, phone: value } : p))}
                  disabled={busy || isTenantUser}
                  defaultCountry="BR"
                  preferredCountries={['BR', 'US', 'PT', 'ES', 'FR', 'GB', 'DE', 'IT']}
                />
              </Grid>
            </Grid>
          </Stack>
      </FormDialog>

      {/* Reactivate Tenant Dialog */}
      <Dialog
        open={reactivateDialogOpen}
        onClose={() => {
          setReactivateDialogOpen(false);
          setReactivatingTenant(null);
        }}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ color: 'success.main' }}>{t('tenants.reactivate.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('tenants.reactivate.message')}
            </Typography>
            {reactivatingTenant && (
              <Typography variant="body1" fontWeight={600}>
                {reactivatingTenant.name} ({reactivatingTenant.code})
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center' }}>
          <CustomButton
            variant="secondary"
            onClick={() => {
              setReactivateDialogOpen(false);
              setReactivatingTenant(null);
            }}
            disabled={busy}
          >
            {t('tenants.reactivate.cancel')}
          </CustomButton>
          <CustomButton onClick={handleReactivate} variant="primary" disabled={busy}>
            {t('tenants.reactivate.confirm')}
          </CustomButton>
        </DialogActions>
      </Dialog>

      {/* Revoke License Dialog */}
      <Dialog
        open={revokeDialogOpen}
        onClose={() => {
          setRevokeDialogOpen(false);
          setRevokingTenant(null);
          setRevokeReason('');
        }}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ color: 'error.main' }}>{t('tenants.revoke.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('tenants.revoke.message')}
            </Typography>
            {revokingTenant && (
              <Typography variant="body1" fontWeight={600}>
                {revokingTenant.name} ({revokingTenant.code})
              </Typography>
            )}
            <TextField
              fullWidth
              label={t('tenants.revoke.reason')}
              placeholder={t('tenants.revoke.reasonPlaceholder')}
              value={revokeReason}
              onChange={(e) => setRevokeReason(e.target.value)}
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center' }}>
          <CustomButton
            variant="secondary"
            onClick={() => {
              setRevokeDialogOpen(false);
              setRevokingTenant(null);
              setRevokeReason('');
            }}
            disabled={busy}
          >
            {t('tenants.revoke.cancel')}
          </CustomButton>
          <CustomButton onClick={handleRevoke} variant="primary" disabled={busy}>
            {t('tenants.revoke.confirm')}
          </CustomButton>
        </DialogActions>
      </Dialog>
      {/* Manage Apps Dialog */}
      <FormDialog
        open={manageAppsOpen}
        onClose={() => {
          setManageAppsOpen(false);
          setManageAppsForm(null);
        }}
        maxWidth="md"
        loading={busy}
        disableBackdropClick
        title={t('tenants.dialogManageAppsTitle', 'Gerenciar Aplicativos e Módulos')}
        icon={<SettingsIcon />}
        onSubmit={handleAppsSubmit}
        submitText={t('common.save')}
        disableSubmit={!manageAppsSchema.safeParse(manageAppsForm).success}
      >
        <Stack sx={{ py: 1 }} spacing={3}>
            {/* Column 1: Application Select */}
            <FormControl fullWidth size="small">
              <InputLabel id="manage-apps-select-label">
                {t('tenants.form.applications')}
              </InputLabel>
              <Select
                labelId="manage-apps-select-label"
                label={t('tenants.form.applications')}
                multiple
                value={manageAppsForm?.applicationIds ?? []}
                onChange={(event) =>
                  setManageAppsForm((prev) => {
                    if (!prev) return prev;
                    const value = event.target.value;
                    const nextIds =
                      typeof value === 'string'
                        ? value.split(',').filter(Boolean)
                        : (value as string[]);
                    const allowedSet = new Set(nextIds);
                    const nextModules = Object.fromEntries(
                      Object.entries(prev.applicationModules ?? {}).filter(([appId]) =>
                        allowedSet.has(appId),
                      ),
                    );
                    return {
                      ...prev,
                      applicationIds: nextIds,
                      applicationModules: nextModules,
                    };
                  })
                }
                renderValue={(selected) => {
                  const ids = Array.isArray(selected) ? selected : [selected];
                  return ids
                    .map((id) => sortedApplications.find((app) => app.id === id)?.name ?? id)
                    .join(', ');
                }}
                disabled={busy}
              >
                {sortedApplications.map((app) => (
                  <MenuItem key={app.id} value={app.id}>
                    <Checkbox checked={manageAppsForm?.applicationIds.includes(app.id) ?? false} />
                    <ListItemText primary={app.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Modules List for Manage Apps */}
            {manageAppsForm &&
              (() => {
                const activeApps = sortedApplications.filter((app) =>
                  manageAppsForm.applicationIds.includes(app.id),
                );
                if (activeApps.length === 0) return null;

                return (
                  <Stack
                    spacing={1}
                    sx={{
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1,
                      p: 2,
                    }}
                  >
                    {(() => {
                      const allSelectedModuleIds = Object.values(
                        manageAppsForm.applicationModules,
                      ).flat();
                      return (
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{
                            p: 1.5,
                            bgcolor: 'action.hover',
                            borderRadius: 1,
                            flexWrap: 'wrap',
                          }}
                        >
                          <CustomButton
                            variant="secondary"
                            onClick={() => {
                              setManageAppsForm((prev) => {
                                if (!prev) return prev;
                                const allModuleIds = activeApps.flatMap((a) =>
                                  (a.modules ?? []).map((m) => m.id),
                                );
                                const defaultDate = prev.isDemo
                                  ? computeDemoLicenseFromDate(prev.createdAt)
                                  : prev.licenseValidUntil;
                                const newExpirations = { ...prev.moduleExpirations };
                                allModuleIds.forEach((mid) => {
                                  if (!newExpirations[mid]) newExpirations[mid] = defaultDate;
                                });
                                const newModules: Record<string, string[]> = {};
                                activeApps.forEach((a) => {
                                  newModules[a.id] = (a.modules ?? []).map((m) => m.id);
                                });
                                return {
                                  ...prev,
                                  applicationModules: newModules,
                                  moduleExpirations: newExpirations,
                                };
                              });
                            }}
                          >
                            {t('common.selectAll', 'Selecionar Todos')}
                          </CustomButton>
                          <CustomButton
                            variant="secondary"
                            aria-label={t(
                              'tenants.a11y.clearModules',
                              'Desmarcar todos os módulos',
                            )}
                            onClick={() => {
                              setManageAppsForm((prev) => {
                                if (!prev) return prev;
                                const clearedModules: Record<string, string[]> = {};
                                activeApps.forEach((a) => {
                                  clearedModules[a.id] = [];
                                });
                                return {
                                  ...prev,
                                  applicationModules: clearedModules,
                                  moduleExpirations: {},
                                };
                              });
                            }}
                          >
                            {t('common.clear', 'Limpar')}
                          </CustomButton>
                          <Box sx={{ flex: 1 }} />
                          <TextField
                            type="date"
                            size="small"
                            label={t('tenants.form.bulkDate', 'Data em lote')}
                            value={bulkDate}
                            onChange={(e) => setBulkDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: todayStr }}
                            sx={{ width: 180 }}
                          />
                          <CustomButton
                            variant="primary"
                            aria-label={t(
                              'tenants.a11y.applyBulkDate',
                              'Aplicar data de validade em lote para todos os módulos selecionados',
                            )}
                            disabled={!bulkDate || allSelectedModuleIds.length === 0}
                            onClick={() => {
                              setManageAppsForm((prev) => {
                                if (!prev || !bulkDate) return prev;
                                const newExpirations: Record<string, string> = {};
                                Object.values(prev.applicationModules)
                                  .flat()
                                  .forEach((mid) => {
                                    newExpirations[mid] = bulkDate;
                                  });
                                return { ...prev, moduleExpirations: newExpirations };
                              });
                              setBulkDate('');
                            }}
                          >
                            {t('tenants.form.applyDate', 'Aplicar')}
                          </CustomButton>
                        </Stack>
                      );
                    })()}
                    <TextField
                      size="small"
                      placeholder={t('tenants.form.searchModule', 'Buscar módulo...')}
                      value={moduleSearch}
                      onChange={(e) => setModuleSearch(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ mb: 1 }}
                    />
                    {(() => {
                      const totalGlobal = activeApps.reduce(
                        (sum, a) => sum + (a.modules?.length ?? 0),
                        0,
                      );
                      const selectedGlobal = Object.values(manageAppsForm.applicationModules).flat()
                        .length;
                      return (
                        <Box role="status" aria-live="polite" aria-atomic="true" sx={{ mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {t('tenants.form.modulesSelected', 'Módulos')}: {selectedGlobal}/
                            {totalGlobal}
                          </Typography>
                        </Box>
                      );
                    })()}
                    {activeApps.map((app) => {
                      const moduleOptions = app.modules ?? [];
                      const selectedModuleIds = manageAppsForm.applicationModules[app.id] ?? [];

                      return (
                        <Accordion
                          key={app.id}
                          disableGutters
                          elevation={0}
                          sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            '&:not(:last-child)': { borderBottom: 0 },
                            '&:before': { display: 'none' },
                          }}
                        >
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              sx={{ width: '100%', mr: 2 }}
                            >
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography fontWeight={600}>{app.name}</Typography>
                                <CustomButton
                                  variant="secondary"
                                  aria-label={t(
                                    'tenants.a11y.selectAllApp',
                                    'Selecionar todos os módulos deste aplicativo',
                                  )}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setManageAppsForm((prev) => {
                                      if (!prev) return prev;
                                      const allModuleIds = (app.modules ?? []).map((m) => m.id);
                                      const defaultDate = prev.isDemo
                                        ? computeDemoLicenseFromDate(prev.createdAt)
                                        : prev.licenseValidUntil;
                                      const newExpirations = { ...prev.moduleExpirations };
                                      allModuleIds.forEach((mid) => {
                                        if (!newExpirations[mid]) newExpirations[mid] = defaultDate;
                                      });
                                      return {
                                        ...prev,
                                        applicationModules: {
                                          ...prev.applicationModules,
                                          [app.id]: allModuleIds,
                                        },
                                        moduleExpirations: newExpirations,
                                      };
                                    });
                                  }}
                                >
                                  {t('common.selectAll', 'Selecionar Todos')}
                                </CustomButton>
                              </Stack>
                              <Chip
                                size="small"
                                color={selectedModuleIds.length > 0 ? 'primary' : 'default'}
                                variant={selectedModuleIds.length > 0 ? 'filled' : 'outlined'}
                                label={`${selectedModuleIds.length}/${moduleOptions.length}`}
                              />
                            </Stack>
                          </AccordionSummary>
                          <AccordionDetails sx={{ pt: 0 }}>
                            {moduleOptions.length > 0 ? (
                              <Stack spacing={0.5}>
                                {(moduleSearch
                                  ? moduleOptions.filter((m) =>
                                      m.name.toLowerCase().includes(moduleSearch.toLowerCase()),
                                    )
                                  : moduleOptions
                                ).map((module) => {
                                  const isChecked = selectedModuleIds.includes(module.id);
                                  const moduleExpiry =
                                    manageAppsForm.moduleExpirations[module.id] || '';
                                  const error = isChecked && !moduleExpiry;

                                  return (
                                    <Stack
                                      key={module.id}
                                      direction="row"
                                      spacing={1}
                                      alignItems="flex-start"
                                    >
                                      <FormControlLabel
                                        control={
                                          <Checkbox
                                            checked={isChecked}
                                            onChange={(event) =>
                                              setManageAppsForm((prev) => {
                                                if (!prev) return prev;
                                                const current = new Set(
                                                  prev.applicationModules[app.id] ?? [],
                                                );
                                                const nextExpirations = {
                                                  ...prev.moduleExpirations,
                                                };

                                                if (event.target.checked) {
                                                  current.add(module.id);
                                                  // Auto-set expiration for demo mode
                                                  if (prev.isDemo && !nextExpirations[module.id]) {
                                                    nextExpirations[module.id] =
                                                      computeDemoLicenseFromDate(prev.createdAt);
                                                  }
                                                } else {
                                                  current.delete(module.id);
                                                  delete nextExpirations[module.id];
                                                }

                                                return {
                                                  ...prev,
                                                  applicationModules: {
                                                    ...prev.applicationModules,
                                                    [app.id]: Array.from(current),
                                                  },
                                                  moduleExpirations: nextExpirations,
                                                };
                                              })
                                            }
                                            disabled={busy}
                                          />
                                        }
                                        label={
                                          <Box
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: 1,
                                              pt: 1,
                                            }}
                                          >
                                            {module.color && (
                                              <Box
                                                sx={{
                                                  width: 10,
                                                  height: 10,
                                                  borderRadius: '50%',
                                                  bgcolor: module.color,
                                                  border: `1px solid ${theme.palette.divider}`,
                                                }}
                                              />
                                            )}
                                            <Typography variant="body2">{module.name}</Typography>
                                          </Box>
                                        }
                                        sx={{
                                          flex: 1,
                                          minWidth: 0,
                                          alignItems: 'flex-start',
                                        }}
                                      />
                                      {isChecked && (
                                        <TextField
                                          error={Boolean(error)}
                                          helperText={
                                            error
                                              ? t('tenants.errors.required', 'Required')
                                              : undefined
                                          }
                                          type="date"
                                          size="small"
                                          label={t('tenants.form.expiresAt', 'Expires')}
                                          value={moduleExpiry}
                                          onChange={(event) =>
                                            setManageAppsForm((prev) =>
                                              prev
                                                ? {
                                                    ...prev,
                                                    moduleExpirations: {
                                                      ...prev.moduleExpirations,
                                                      [module.id]: event.target.value,
                                                    },
                                                  }
                                                : prev,
                                            )
                                          }
                                          disabled={busy}
                                          InputLabelProps={{ shrink: true }}
                                          inputProps={{ min: todayStr }}
                                          sx={{ width: 160, mt: 0.5 }}
                                        />
                                      )}
                                    </Stack>
                                  );
                                })}
                              </Stack>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                {t('tenants.form.modulesEmptyForApplication')}
                              </Typography>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      );
                    })}
                  </Stack>
                );
              })()}
          </Stack>
      </FormDialog>
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>{t('tenants.revocationDetails.title', 'Revocation Details')}</DialogTitle>
        <DialogContent dividers>
          {revocationDetails ? (
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('tenants.revocationDetails.date', 'Date')}
                </Typography>
                <Typography variant="body1">
                  {revocationDetails.revokedAt
                    ? new Date(revocationDetails.revokedAt).toLocaleString()
                    : '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('tenants.revocationDetails.by', 'Revoked By')}
                </Typography>
                <Typography variant="body1">
                  {revocationDetails.revokedByUsername || '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {t('tenants.revocationDetails.reason', 'Reason')}
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>
                  {revocationDetails.reason ||
                    t('tenants.revocationDetails.noReason', 'No reason provided')}
                </Typography>
              </Box>
            </Stack>
          ) : (
            <Typography>{t('common.loading')}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <CustomButton variant="secondary" onClick={() => setDetailsDialogOpen(false)}>
            {t('common.close')}
          </CustomButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
